/**
 * Web アプリの設定。
 * ビルド時の環境変数 (VITE_*) から読み込む。
 */
import type { AdsConfig } from "@spli/app-logic";
import { ADSENSE_ADS_ENABLED, ADSENSE_PUBLISHER_ID, ADSENSE_SLOTS } from "./adsense.js";

/**
 * API のベース URL。
 *
 * 本番では Web と API を CloudFront の同一オリジンから配る (/api/* だけ API Gateway
 * へ転送している) ので、既定は**空文字 = 相対 URL**にしておく。
 * こうすると spli.app でも www.spli.app でも、開いているホストへそのまま問い合わせる。
 *
 * ローカル開発だけ、別ポートで動く API を指す。
 * VITE_API_BASE_URL を明示すればどちらも上書きできる。
 */
export const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? "http://localhost:4000" : "");

/**
 * 広告設定。
 *
 * Web では AdMob と同じ Google のネットワーク (AdSense) を使う。
 *
 * パブリッシャー ID はサイトに紐づく固定値なので、ビルドでは既定で
 * `ADSENSE_PUBLISHER_ID` を使う (index.html に差し込む配信スクリプトと同じ値)。
 * 開発サーバーでは配信スクリプトを読み込まないので、ここも未設定にして
 * プレースホルダーだけを表示する。
 *
 * 掲載面ごとのスロット ID も同じ理由でリポジトリに持ち (ADSENSE_SLOTS)、
 * VITE_ADSENSE_SLOT_* が渡されたときだけそちらを使う。
 *
 * 枠を出すかどうかは ADSENSE_ADS_ENABLED で決める (現在は無効)。
 * ID と掲載面の設定はそのまま残し、フラグを戻すだけで再開できるようにしてある。
 */
const clientId =
  import.meta.env.VITE_ADSENSE_CLIENT_ID ??
  (import.meta.env.DEV ? undefined : ADSENSE_PUBLISHER_ID);

/** 環境変数は「空文字 = 指定なし」として扱う (未設定と同じにする) */
const slotId = (value: string | undefined, fallback: string): string =>
  value !== undefined && value.trim() !== "" ? value : fallback;

export const ADS_CONFIG: AdsConfig = {
  enabled: ADSENSE_ADS_ENABLED && Boolean(clientId),
  clientId,
  testMode: import.meta.env.DEV,
  units: {
    "expense-list": {
      unitId: slotId(import.meta.env.VITE_ADSENSE_SLOT_LIST, ADSENSE_SLOTS.expenseList),
      format: "banner",
    },
    settlement: {
      unitId: slotId(import.meta.env.VITE_ADSENSE_SLOT_SETTLEMENT, ADSENSE_SLOTS.settlement),
      format: "rectangle",
    },
  },
};
